import { AxiosInstance } from 'axios';
import { Language, mapReplacer } from '..';
import i18n from '../../../client/src/i18n/i18n';
import { NotificationType } from '../notification/notification.types';
import { generateNotification } from '../notification/notification.util';
import { InventoryEntry, InventoryGroup } from './Inventory.types';

/**
 * API Method to get a csv file containing an overview about all inventoryItems
 * @param axios instance of axios
 * @returns Promise containing the svg file or undefined on error
 */
export const getInventoryOverviewCsv = async (
  axios: AxiosInstance,
  language: Language,
  categoryIds: string[],
  includeDeactivated: boolean,
): Promise<Blob | undefined> => {
  return axios
    .get('/export/csv/inventory', {
      params: {
        language,
        categoryIds: categoryIds.toString(),
        includeDeactivated,
      },
      responseType: 'blob',
    })
    .then((res) => new Blob([res.data], { type: 'text/csv;charset=utf-8' }))
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getOverviewCsv'),
      });
      console.error('Error while loading csv for inventoryItems', exc);
      return undefined;
    });
};

/**
 * API Method to load all inventoryEntries for a gicen group
 * @param axios instance of axios
 * @param group the group to load
 * @returns Array containing all found entries or empty array on error
 */
export const getAllInventoryEntriesForGroup = async (
  axios: AxiosInstance,
  group: InventoryGroup,
  includeDeactivated: boolean,
): Promise<InventoryEntry[]> => {
  return axios
    .get('/inventory/entry/group', { params: { group, includeDeactivated } })
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getInventoryEntries'),
      });
      console.error('Error hile loading inventoryEntries', exc);
      return [];
    });
};
export const getAllInventoryEntries = async (axios: AxiosInstance): Promise<InventoryEntry[]> => {
  return axios
    .get('/inventory/entry/all')
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getInventoryEntries'),
      });
      console.error('Error hile loading inventoryEntries', exc);
      return [];
    });
};

/**
 * API Method to update an existing inventoryEntry
 * @param axios instance of axios
 * @param entry the client side updated instance
 * @returns The server side updated instance or undefined on error
 */
export const updateInventoryEntry = async (
  axios: AxiosInstance,
  entry: InventoryEntry,
): Promise<InventoryEntry | undefined> => {
  return axios
    .post('/inventory/entry/update', JSON.stringify(entry, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      console.error(exc);
      generateNotification({
        value: i18n.t('general.notification.error.inventoryItemUpdateError'),
        type: NotificationType.ERROR,
      });
      return undefined;
    });
};

/**
 * POST Endpoint to create a new inventoryEntry
 * @param axios instance of axios
 * @param entry the client side generated entry
 * @returns server side updated entry or undefined on error
 */
export const createNewInventoryEntry = async (
  axios: AxiosInstance,
  entry: InventoryEntry,
): Promise<InventoryEntry | undefined> => {
  return axios
    .post('/inventory/entry/', JSON.stringify(entry, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      console.error(exc);
      generateNotification({
        value: i18n.t('general.notification.error.inventoryItemUpdateError'),
        type: NotificationType.ERROR,
      });
      return undefined;
    });
};

/**
 * API Method to deleta an inventory entry
 * @param axios instance of axios
 * @param id the id of the entry to delete
 * @returns Promsie containing a boolean for success
 */
export const deleteInventoryEntry = async (axios: AxiosInstance, id: string): Promise<boolean> => {
  return axios
    .post('/inventory/entry/delete', id, {
      headers: { 'Content-Type': 'text/plain' },
    })
    .then((res) => res.status === 200)
    .catch((exc) => {
      console.error('Error while deleting inventoryItem', exc);
      generateNotification({
        value: i18n.t('general.notification.error.inventoryItemUpdateError'),
        type: NotificationType.ERROR,
      });
      return false;
    });
};
